import { AlertTriangle } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { useIsOrgAdmin } from "@/stores";
import type { UseCase, ReadinessReason } from "@/features/llm/types";

interface LlmSetupRequiredCardProps {
  useCase: UseCase;
  reason?: ReadinessReason | null;
  className?: string;
}

/**
 * Displayed in place of a feature trigger when LLM readiness check fails
 * for the given use-case.
 *
 * CTA is role-aware: org admins (owner or admin role) get a navigation link
 * to the /llm workspace; members get a static "contact your admin" label.
 * The /llm route is admin-gated, so non-admins must never receive a link to it.
 *
 * A single user-visible copy is shown regardless of the reason slug — reason
 * is surfaced only in the DEV tooltip so engineers can debug without exposing
 * internal enum values to end users.
 */
export function LlmSetupRequiredCard({
  useCase: _useCase,
  reason,
  className,
}: LlmSetupRequiredCardProps) {
  const t = useTranslations("llmSetup");
  const isAdmin = useIsOrgAdmin();

  return (
    <div
      role="alert"
      aria-label={t("setup_required.title")}
      className={[
        "flex items-start gap-3 rounded-lg border border-[var(--destructive)]/40 bg-[var(--destructive)]/5 p-4",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <AlertTriangle
        className="mt-0.5 h-4 w-4 shrink-0 text-[var(--destructive)]"
        aria-hidden="true"
      />
      <div className="flex-1 space-y-2">
        <p className="text-sm font-medium leading-tight">
          {t("setup_required.title")}
        </p>
        <p className="text-xs text-[var(--muted-foreground)]">
          {t("setup_required.description")}
        </p>
        {isAdmin ? (
          <Link
            to="/llm"
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 text-xs font-medium hover:bg-[var(--muted)]"
          >
            {t("setup_required.cta_admin")}
          </Link>
        ) : (
          <span className="text-xs text-[var(--muted-foreground)]">
            {t("setup_required.cta_member")}
          </span>
        )}
        {import.meta.env.DEV && reason && (
          <span
            title={`reason: ${reason}`}
            className="text-xs text-[var(--muted-foreground)]"
          >
            [DEV: {reason}]
          </span>
        )}
      </div>
    </div>
  );
}
